import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  BarElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

import { hexToRgb } from 'common/styles/colors';

const OutcomesBarChart = ({ lineChartData }) => {
  // Register the required elements for Bar Chart
  ChartJS.register(BarElement, LinearScale, Title, Tooltip, Legend, CategoryScale);

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });

  useEffect(() => {
    if (lineChartData && lineChartData.length > 0) {
      const filteredLineChartData = lineChartData.filter(dataSet =>
        Array.isArray(dataSet.data) &&
        dataSet.data.length > 0 &&
        dataSet.data.some(item => item && item.overall_score != null)
      );

      if (filteredLineChartData.length === 0) {
        setChartData({ labels: [], datasets: [] });
        return;
      }

      const groupedData = {};

      // Keep period labels in chronological order using the source start_date values.
      const allLabels = Array.from(
        new Map(
          filteredLineChartData
            .slice()
            .sort((a, b) => {
              const aTime = Date.parse(a.start_date || '');
              const bTime = Date.parse(b.start_date || '');

              if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
                return aTime - bTime;
              }

              return a.name.localeCompare(b.name);
            })
            .map(dataSet => [dataSet.name, dataSet.name])
        ).values()
      );

      // Initialize groupedData to ensure all labels exist for each option_name
      filteredLineChartData.forEach(dataSet => {
        dataSet.data.forEach(item => {
          const { option_name, overall_score, color } = item;

          if (!groupedData[option_name]) {
            groupedData[option_name] = {
              data: allLabels.map(() => null),
              counts: allLabels.map(() => null),
              backgroundColor: hexToRgb(color, 0.6),
              borderColor: hexToRgb(color, 1),
            };
          }

          // Assign values in the correct position
          const index = allLabels.indexOf(dataSet.name);
          if (index !== -1) {
            groupedData[option_name].data[index] = parseFloat(overall_score.toFixed(1));
            groupedData[option_name].counts[index] = item.n;
          }
        });
      });

      // Format data for the chart and sort bar series alphabetically by option name
      const formattedData = Object.keys(groupedData)
        .sort((a, b) => a.localeCompare(b))
        .map(optionName => ({
          label: optionName,
          data: groupedData[optionName].data,
          counts: groupedData[optionName].counts,
          backgroundColor: groupedData[optionName].backgroundColor,
          borderColor: groupedData[optionName].borderColor,
          borderWidth: 1,
        }));

      setChartData({
        labels: allLabels,
        datasets: formattedData,
      });
    }
  }, [lineChartData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          font: {
            family: 'Poppins',
            size: 12,
          },
        },
      },
      tooltip: {
        bodyFont: {
          family: 'Poppins',
          size: 12,
        },
        titleFont: {
          family: 'Poppins',
          size: 14,
        },
        callbacks: {
          title: function (context) {
            return context[0].dataset.label || '';
          },
          label: function (context) {
            const score = context.raw;
            const dataset = context.dataset;
            const index = context.dataIndex;
            const n = dataset.counts ? dataset.counts[index] : null;

            return `Score: ${score} (${n ?? 'n/a'} repondents)`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: false,
          text: 'Quarters',
          font: {
            family: 'Poppins',
            size: 14,
          },
        },
        ticks: {
          font: {
            family: 'Poppins',
            size: 12,
          },
        },
      },
      y: {
        min: 0,
        max: 10,
        title: {
          display: true,
          text: 'Overall Score',
          font: {
            family: 'Poppins',
            size: 11,
          },
        },
        ticks: {
          font: {
            family: 'Poppins',
            size: 12,
          },
        },
      },
    },
  };

  return <Bar data={chartData} options={options} />;
};

export default OutcomesBarChart;
